/**
 * Route manifest: the one list of admin sections, their URLs and their
 * sidebar entries.
 *
 * App.tsx maps it to guarded routes (see `renderProtectedRoutes`) and Layout
 * derives the sidebar from it, so a section's URL gate and its nav link can't
 * disagree — they are read from the same row. Every `SectionKey` appears here
 * exactly once; `src/routes/__tests__/manifest.test.tsx` asserts that.
 *
 * Two different "groups" live side by side and are deliberately not the same
 * thing:
 * - `PERMISSIONS[...].group` (types/permissions.ts) is WHO gets a section:
 *   the matrix heading, the grant-only list, the sensitive-grant warning.
 * - `nav.group` below is only WHERE the link sits in the sidebar.
 * BPA is the one section where they differ: grantable per person like every
 * other operations section, but shelved under System in the sidebar next to
 * the other back-office tools.
 */
import type { ComponentType } from 'react';
import type { LucideIcon } from 'lucide-react';
import {
  Users, Calendar, UserCog, LayoutDashboard, ClipboardCheck, Palette, PieChart, CreditCard, History, Tags,
  UserCheck, MapPin, Cake, Bot, Share2, Timer, Dumbbell, Package, Gift, Contact, Boxes, DoorOpen, KeyRound,
  Newspaper, MessageSquare, BellRing,
} from 'lucide-react';
import { createLazyRoute } from '@/utils/routeUtils';
import type { SectionKey } from '@/types/permissions';

const DashboardPage = createLazyRoute(() => import('@/pages/dashboard/DashboardPage'));
const MembersPage = createLazyRoute(() => import('@/pages/members/MembersPage'));
const MemberViewPage = createLazyRoute(() => import('@/pages/members/MemberViewPage'));
const NewMemberPage = createLazyRoute(() => import('@/pages/members/NewMemberPage'));
const EditMemberPage = createLazyRoute(() => import('@/pages/members/EditMemberPage'));
const DeletedMembersPage = createLazyRoute(() => import('@/pages/members/DeletedMembersPage'));
const ClassesPage = createLazyRoute(() => import('@/pages/classes/ClassesPage'));
const ClassViewPage = createLazyRoute(() => import('@/pages/classes/ClassViewPage'));
const NoShowSeatsGuidePage = createLazyRoute(() => import('@/pages/help/NoShowSeatsGuidePage'));
const NewClassPage = createLazyRoute(() => import('@/pages/classes/NewClassPage'));
const EditClassPage = createLazyRoute(() => import('@/pages/classes/EditClassPage'));
const CallBoothsPage = createLazyRoute(() => import('@/pages/call-booths/CallBoothsPage'));
const LockersPage = createLazyRoute(() => import('@/pages/lockers/LockersPage'));
const ClassCategoriesPage = createLazyRoute(() => import('@/pages/class-categories/ClassCategoriesPage'));
const NewClassCategoryPage = createLazyRoute(() => import('@/pages/class-categories/NewClassCategoryPage'));
const ClassCategoryViewPage = createLazyRoute(() => import('@/pages/class-categories/ClassCategoryViewPage'));
const EditClassCategoryPage = createLazyRoute(() => import('@/pages/class-categories/EditClassCategoryPage'));
const ClassLocationsPage = createLazyRoute(() => import('@/pages/class-locations/ClassLocationsPage'));
const NewClassLocationPage = createLazyRoute(() => import('@/pages/class-locations/NewClassLocationPage'));
const ClassLocationViewPage = createLazyRoute(() => import('@/pages/class-locations/ClassLocationViewPage'));
const EditClassLocationPage = createLazyRoute(() => import('@/pages/class-locations/EditClassLocationPage'));
const CoachesPage = createLazyRoute(() => import('@/pages/coaches/CoachesPage'));
const NewCoachPage = createLazyRoute(() => import('@/pages/coaches/NewCoachPage'));
const CoachViewPage = createLazyRoute(() => import('@/pages/coaches/CoachViewPage'));
const EditCoachPage = createLazyRoute(() => import('@/pages/coaches/EditCoachPage'));
const EmployeesPage = createLazyRoute(() => import('@/pages/employees/EmployeesPage'));
const EmployeesOrgChartPage = createLazyRoute(() => import('@/pages/employees/EmployeesOrgChartPage'));
const EmployeeViewPage = createLazyRoute(() => import('@/pages/employees/EmployeeViewPage'));
const InventoryPage = createLazyRoute(() => import('@/pages/inventory/InventoryPage'));
const MembershipsPage = createLazyRoute(() => import('@/pages/memberships/MembershipsPage'));
const MembershipViewPage = createLazyRoute(() => import('@/pages/memberships/MembershipViewPage'));
const EditMembershipPage = createLazyRoute(() => import('@/pages/memberships/EditMembershipPage'));
const NewMembershipPage = createLazyRoute(() => import('@/pages/memberships/NewMembershipPage'));
const MembershipTypesPage = createLazyRoute(() => import('@/pages/membership-types/MembershipTypesPage'));
const NewMembershipTypePage = createLazyRoute(() => import('@/pages/membership-types/NewMembershipTypePage'));
const MembershipTypeViewPage = createLazyRoute(() => import('@/pages/membership-types/MembershipTypeViewPage'));
const EditMembershipTypePage = createLazyRoute(() => import('@/pages/membership-types/EditMembershipTypePage'));
const AdminsPage = createLazyRoute(() => import('@/pages/admins/AdminsPage'));
const AdminUserViewPage = createLazyRoute(() => import('@/pages/admins/AdminUserViewPage'));
const EditAdminUserPage = createLazyRoute(() => import('@/pages/admins/EditAdminUserPage'));
const NewAdminUserPage = createLazyRoute(() => import('@/pages/admins/NewAdminUserPage'));
const RolePermissionsPage = createLazyRoute(() => import('@/pages/admins/RolePermissionsPage'));
const CheckInsPage = createLazyRoute(() => import('@/pages/check-ins/CheckInsPage'));
const BirthdaysPage = createLazyRoute(() => import('@/pages/birthdays/BirthdaysPage'));
const AgentPage = createLazyRoute(() => import('@/pages/agent/AgentPage'));
const ActivityLogsPage = createLazyRoute(() => import('@/pages/activity-logs/ActivityLogsPage'));
const FeedbackPage = createLazyRoute(() => import('@/pages/feedback/FeedbackPage'));
const StyleBookPage = createLazyRoute(() => import('@/pages/StyleBookPage'));
const ReportsPage = createLazyRoute(() => import('@/pages/reports/ReportsPage'));
const OdooPage = createLazyRoute(() => import('@/pages/odoo/OdooPage'));
const PersonalTrainingPage = createLazyRoute(() => import('@/pages/personal-training/PersonalTrainingPage'));
const NewPTSessionPage = createLazyRoute(() => import('@/pages/pt-sessions/NewPTSessionPage'));
const PTSessionViewPage = createLazyRoute(() => import('@/pages/pt-sessions/PTSessionViewPage'));
const EditPTSessionPage = createLazyRoute(() => import('@/pages/pt-sessions/EditPTSessionPage'));
const NewPTPackPage = createLazyRoute(() => import('@/pages/pt-packs/NewPTPackPage'));
const PTPackViewPage = createLazyRoute(() => import('@/pages/pt-packs/PTPackViewPage'));
const EditPTPackPage = createLazyRoute(() => import('@/pages/pt-packs/EditPTPackPage'));
const PTPackTypesPage = createLazyRoute(() => import('@/pages/pt-pack-types/PTPackTypesPage'));
const NewPTPackTypePage = createLazyRoute(() => import('@/pages/pt-pack-types/NewPTPackTypePage'));
const PTPackTypeViewPage = createLazyRoute(() => import('@/pages/pt-pack-types/PTPackTypeViewPage'));
const EditPTPackTypePage = createLazyRoute(() => import('@/pages/pt-pack-types/EditPTPackTypePage'));
const GiftCardsPage = createLazyRoute(() => import('@/pages/gift-cards/GiftCardsPage'));
const NewGiftCardPage = createLazyRoute(() => import('@/pages/gift-cards/NewGiftCardPage'));
const GiftCardViewPage = createLazyRoute(() => import('@/pages/gift-cards/GiftCardViewPage'));
const NewsPage = createLazyRoute(() => import('@/pages/news/NewsPage'));
const NewNewsPostPage = createLazyRoute(() => import('@/pages/news/NewNewsPostPage'));
const NewsPostViewPage = createLazyRoute(() => import('@/pages/news/NewsPostViewPage'));
const EditNewsPostPage = createLazyRoute(() => import('@/pages/news/EditNewsPostPage'));
const PushNotificationsPage = createLazyRoute(() => import('@/pages/push-notifications/PushNotificationsPage'));
const NewPushCampaignPage = createLazyRoute(() => import('@/pages/push-notifications/NewPushCampaignPage'));
const PushCampaignViewPage = createLazyRoute(() => import('@/pages/push-notifications/PushCampaignViewPage'));
const BpaPage = createLazyRoute(() => import('@/pages/bpa/BpaPage'));
const BpaViewPage = createLazyRoute(() => import('@/pages/bpa/BpaViewPage'));
const NewBpaPage = createLazyRoute(() => import('@/pages/bpa/NewBpaPage'));
const EditBpaPage = createLazyRoute(() => import('@/pages/bpa/EditBpaPage'));

/**
 * One URL of a section: either a page, or a redirect kept so an old bookmark
 * or link still lands (`redirectTo` may carry a query string).
 */
export type SectionRoute =
  | {
      path: string;
      page: ComponentType;
      /**
       * Route param that, when it equals the signed-in admin's own id, lets
       * the route through regardless of the section. See RequireSection.
       */
      allowSelfParam?: string;
    }
  | { path: string; redirectTo: string };

/**
 * Where a link sits in the sidebar. `main` is the top list; `admin` is the
 * flat list below it; `configuration` and `system` are the collapsible groups
 * at the bottom, in that order.
 */
export type NavGroup = 'main' | 'admin' | 'configuration' | 'system';

export interface SectionNav {
  name: string;
  /** Must be one of the section's own page routes. */
  href: string;
  icon: LucideIcon;
  group: NavGroup;
}

export interface SectionManifestEntry {
  section: SectionKey;
  /** Omitted for sections reached only from another page (PT sessions/packs). */
  nav?: SectionNav;
  routes: readonly SectionRoute[];
}

/** Sidebar order within each nav group is the order below. */
export const ROUTE_MANIFEST: readonly SectionManifestEntry[] = [
  // --- Sidebar: main ---------------------------------------------------------
  {
    section: 'DASHBOARD',
    nav: { name: 'Dashboard', href: '/dashboard', icon: LayoutDashboard, group: 'main' },
    routes: [
      { path: '/', redirectTo: '/dashboard' },
      { path: '/dashboard', page: DashboardPage },
    ],
  },
  {
    section: 'MEMBERS',
    nav: { name: 'Members', href: '/members', icon: Users, group: 'main' },
    routes: [
      { path: '/members', page: MembersPage },
      { path: '/members/new', page: NewMemberPage },
      { path: '/members/:id', page: MemberViewPage },
      { path: '/members/:id/edit', page: EditMemberPage },
      { path: '/members/deleted', page: DeletedMembersPage },
    ],
  },
  {
    section: 'CLASSES',
    nav: { name: 'Classes', href: '/classes', icon: Calendar, group: 'main' },
    routes: [
      { path: '/classes', page: ClassesPage },
      { path: '/classes/new', page: NewClassPage },
      { path: '/classes/:id/view', page: ClassViewPage },
      { path: '/classes/:id/edit', page: EditClassPage },
      // Staff guide linked from the class page; gated with the section it
      // explains. No sidebar entry — it is reached from where it is needed.
      { path: '/help/no-show-seats', page: NoShowSeatsGuidePage },
    ],
  },
  {
    section: 'MEMBERSHIPS',
    nav: { name: 'Memberships', href: '/memberships', icon: CreditCard, group: 'main' },
    routes: [
      { path: '/memberships', page: MembershipsPage },
      { path: '/memberships/new', page: NewMembershipPage },
      { path: '/memberships/:id', page: MembershipViewPage },
      { path: '/memberships/:id/edit', page: EditMembershipPage },
    ],
  },
  {
    section: 'PERSONAL_TRAINING',
    nav: { name: 'Personal Training', href: '/personal-training', icon: Dumbbell, group: 'main' },
    routes: [{ path: '/personal-training', page: PersonalTrainingPage }],
  },
  {
    section: 'PT_SESSIONS',
    routes: [
      { path: '/pt-sessions', redirectTo: '/personal-training?tab=sessions' },
      { path: '/pt-sessions/new', page: NewPTSessionPage },
      { path: '/pt-sessions/:id', page: PTSessionViewPage },
      { path: '/pt-sessions/:id/edit', page: EditPTSessionPage },
    ],
  },
  {
    section: 'PT_PACKS',
    routes: [
      { path: '/pt-packs', redirectTo: '/personal-training' },
      { path: '/pt-packs/new', page: NewPTPackPage },
      { path: '/pt-packs/:id', page: PTPackViewPage },
      { path: '/pt-packs/:id/edit', page: EditPTPackPage },
    ],
  },
  {
    section: 'GIFT_CARDS',
    nav: { name: 'Gift Cards', href: '/gift-cards', icon: Gift, group: 'main' },
    routes: [
      { path: '/gift-cards', page: GiftCardsPage },
      { path: '/gift-cards/new', page: NewGiftCardPage },
      { path: '/gift-cards/:id', page: GiftCardViewPage },
    ],
  },
  {
    section: 'CHECK_INS',
    nav: { name: 'Check-Ins', href: '/check-ins', icon: ClipboardCheck, group: 'main' },
    routes: [{ path: '/check-ins', page: CheckInsPage }],
  },
  {
    section: 'SPACES',
    nav: { name: 'Spaces', href: '/spaces', icon: DoorOpen, group: 'main' },
    routes: [
      { path: '/spaces', page: CallBoothsPage },
      // Renamed when the Library meeting room joined the booths. Kept so
      // existing bookmarks and the old nav link still land.
      { path: '/call-booths', redirectTo: '/spaces' },
    ],
  },
  {
    section: 'LOCKERS',
    nav: { name: 'Lockers', href: '/lockers', icon: KeyRound, group: 'main' },
    routes: [{ path: '/lockers', page: LockersPage }],
  },
  {
    section: 'BIRTHDAYS',
    nav: { name: 'Birthdays', href: '/birthdays', icon: Cake, group: 'main' },
    routes: [{ path: '/birthdays', page: BirthdaysPage }],
  },
  {
    section: 'AGENT',
    nav: { name: 'Agent', href: '/agent', icon: Bot, group: 'main' },
    routes: [{ path: '/agent', page: AgentPage }],
  },
  {
    section: 'NEWS',
    nav: { name: 'News', href: '/news', icon: Newspaper, group: 'main' },
    routes: [
      { path: '/news', page: NewsPage },
      { path: '/news/new', page: NewNewsPostPage },
      { path: '/news/:id', page: NewsPostViewPage },
      { path: '/news/:id/edit', page: EditNewsPostPage },
    ],
  },

  // --- Sidebar: admin --------------------------------------------------------
  {
    section: 'REPORTS',
    nav: { name: 'Reports', href: '/reports', icon: PieChart, group: 'admin' },
    routes: [{ path: '/reports', page: ReportsPage }],
  },
  {
    section: 'FEEDBACK',
    nav: { name: 'Feedback', href: '/feedback', icon: MessageSquare, group: 'admin' },
    routes: [{ path: '/feedback', page: FeedbackPage }],
  },
  {
    section: 'PUSH_NOTIFICATIONS',
    nav: { name: 'Push notifications', href: '/push-notifications', icon: BellRing, group: 'admin' },
    routes: [
      { path: '/push-notifications', page: PushNotificationsPage },
      { path: '/push-notifications/new', page: NewPushCampaignPage },
      { path: '/push-notifications/:id', page: PushCampaignViewPage },
    ],
  },
  {
    section: 'ADMIN_USERS',
    nav: { name: 'Admin Users', href: '/admin-users', icon: UserCog, group: 'admin' },
    routes: [
      { path: '/admin-users', page: AdminsPage },
      { path: '/admin-users/new', page: NewAdminUserPage },
      { path: '/admin-users/permissions', page: RolePermissionsPage },
      // An admin may open and edit their OWN record whatever their tier
      // (update-admin-user allows self-edit of name/email).
      { path: '/admin-users/:id', page: AdminUserViewPage, allowSelfParam: 'id' },
      { path: '/admin-users/:id/edit', page: EditAdminUserPage, allowSelfParam: 'id' },
    ],
  },
  {
    section: 'COACHES',
    nav: { name: 'Coaches', href: '/coaches', icon: UserCheck, group: 'admin' },
    routes: [
      { path: '/coaches', page: CoachesPage },
      { path: '/coaches/new', page: NewCoachPage },
      { path: '/coaches/:id', page: CoachViewPage },
      { path: '/coaches/:id/edit', page: EditCoachPage },
    ],
  },
  {
    section: 'EMPLOYEES',
    nav: { name: 'Employees', href: '/employees', icon: Contact, group: 'admin' },
    routes: [
      { path: '/employees', page: EmployeesPage },
      { path: '/employees/org-chart', page: EmployeesOrgChartPage },
      { path: '/employees/:id', page: EmployeeViewPage },
    ],
  },
  {
    section: 'INVENTORY',
    nav: { name: 'Inventory', href: '/inventory', icon: Boxes, group: 'admin' },
    routes: [{ path: '/inventory', page: InventoryPage }],
  },
  {
    section: 'ACTIVITY_LOGS',
    nav: { name: 'Activity Logs', href: '/activity-logs', icon: History, group: 'admin' },
    routes: [{ path: '/activity-logs', page: ActivityLogsPage }],
  },

  // --- Sidebar: Configuration (collapsible) ----------------------------------
  // Catalog/type-definition pages — rarely touched, set-and-forget config.
  {
    section: 'MEMBERSHIP_TYPES',
    nav: { name: 'Membership Types', href: '/membership-types', icon: CreditCard, group: 'configuration' },
    routes: [
      { path: '/membership-types', page: MembershipTypesPage },
      { path: '/membership-types/new', page: NewMembershipTypePage },
      { path: '/membership-types/:id', page: MembershipTypeViewPage },
      { path: '/membership-types/:id/edit', page: EditMembershipTypePage },
    ],
  },
  {
    section: 'CLASS_CATEGORIES',
    nav: { name: 'Class Categories', href: '/class-categories', icon: Tags, group: 'configuration' },
    routes: [
      { path: '/class-categories', page: ClassCategoriesPage },
      { path: '/class-categories/new', page: NewClassCategoryPage },
      { path: '/class-categories/:id', page: ClassCategoryViewPage },
      { path: '/class-categories/:id/edit', page: EditClassCategoryPage },
    ],
  },
  {
    section: 'CLASS_LOCATIONS',
    nav: { name: 'Class Locations', href: '/class-locations', icon: MapPin, group: 'configuration' },
    routes: [
      { path: '/class-locations', page: ClassLocationsPage },
      { path: '/class-locations/new', page: NewClassLocationPage },
      { path: '/class-locations/:id', page: ClassLocationViewPage },
      { path: '/class-locations/:id/edit', page: EditClassLocationPage },
    ],
  },
  {
    section: 'PT_PACK_TYPES',
    nav: { name: 'PT Pack Types', href: '/pt-pack-types', icon: Package, group: 'configuration' },
    routes: [
      { path: '/pt-pack-types', page: PTPackTypesPage },
      { path: '/pt-pack-types/new', page: NewPTPackTypePage },
      { path: '/pt-pack-types/:id', page: PTPackTypeViewPage },
      { path: '/pt-pack-types/:id/edit', page: EditPTPackTypePage },
    ],
  },

  // --- Sidebar: System (collapsible) -----------------------------------------
  // Integration / system / dev pages.
  {
    section: 'STYLE_BOOK',
    nav: { name: 'Style Book', href: '/style-book', icon: Palette, group: 'system' },
    routes: [{ path: '/style-book', page: StyleBookPage }],
  },
  {
    section: 'ODOO',
    nav: { name: 'Odoo', href: '/odoo', icon: Share2, group: 'system' },
    routes: [{ path: '/odoo', page: OdooPage }],
  },
  {
    section: 'BPA',
    nav: { name: 'BPA', href: '/bpa', icon: Timer, group: 'system' },
    routes: [
      { path: '/bpa', page: BpaPage },
      { path: '/bpa/new', page: NewBpaPage },
      { path: '/bpa/:id', page: BpaViewPage },
      { path: '/bpa/:id/edit', page: EditBpaPage },
    ],
  },
];

export type NavItem = SectionNav & { section: SectionKey };

/** Every sidebar link, in manifest order. */
export const NAV_ITEMS: readonly NavItem[] = ROUTE_MANIFEST.flatMap((entry) =>
  entry.nav ? [{ ...entry.nav, section: entry.section }] : [],
);
